import { useEffect, useState } from 'react'

import { useAuth } from '../contexts/AuthContext'
import { adminFeedbackApi } from '../services/feedbackApi'
import type { AdminFeedbackCounts } from '../types/feedback'

const POLL_INTERVAL_MS = 60_000
const ZERO: AdminFeedbackCounts = { new_total: 0, new_today: 0 }

/**
 * Poll the admin feedback counts every 60s for nav badges + tiles.
 * Only fetches when the caller is EVO_ADMIN or EVO_TEAM - PSC_TEAM and
 * FP_USER never need the count and the endpoint would 403 anyway.
 */
export function useFeedbackCounts(): {
  counts: AdminFeedbackCounts
  loading: boolean
  refresh: () => void
} {
  const { user } = useAuth()
  const role = user?.role
  const eligible = role === 'EVO_ADMIN' || role === 'EVO_TEAM'

  const [counts, setCounts] = useState<AdminFeedbackCounts>(ZERO)
  const [loading, setLoading] = useState<boolean>(eligible)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    if (!eligible) {
      setCounts(ZERO)
      setLoading(false)
      return
    }

    let cancelled = false
    const fetchCounts = async () => {
      try {
        const data = await adminFeedbackApi.counts()
        if (!cancelled) setCounts(data)
      } catch {
        // Silent - the badge just stays at its last good value. Don't
        // throw a toast for a polling failure.
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchCounts()
    const handle = window.setInterval(fetchCounts, POLL_INTERVAL_MS)
    return () => {
      cancelled = true
      window.clearInterval(handle)
    }
  }, [eligible, tick])

  return {
    counts,
    loading,
    refresh: () => setTick(t => t + 1),
  }
}
